import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
  distinctUntilChanged,
  map,
  pluck,
  skip,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { AuthHelpers, AuthService, CompanySwitchStrategy } from '@core/http-legacy';
import { resolveMemberOrgGuid, TellentCurrentUserSelectors } from '@core/tellent-data';
import { TELLENT_SUITE_ENABLED } from '@core/tellent-types';
import { setDefaultTimezone } from '@core/time-legacy';
import { WindowRef } from '@core/user-interface-legacy';

import { AccountActions, AccountSelectors } from './account.state';
import { CurrentUserHttpClient } from '../store/current-user/current-user-http-client';
import {
  CurrentUserEntityActions,
  CurrentUserSelectors,
} from '../store/current-user/current-user.store';

@Injectable({ providedIn: 'root' })
export class AccountEffects {
  private readonly actions$ = inject(Actions);

  private readonly store = inject(Store);
  public currentUserHttp = inject(CurrentUserHttpClient);
  public authService = inject(AuthService);
  public authHelpers = inject(AuthHelpers);
  public windowRef = inject(WindowRef);
  public companySwitch = inject(CompanySwitchStrategy);
  public tellentSuiteEnabled = inject(TELLENT_SUITE_ENABLED);
  public tellentCurrentUser = this.store.selectSignal(TellentCurrentUserSelectors.data);

  public deleteAccountRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.deleteRequest),
      switchMap(action =>
        this.currentUserHttp.deleteAccountRequest(action.payload).pipe(
          filterBoolean,
          map(() =>
            AccountActions.signOut({
              payload: { afterSignOutUrl: this.authService.getAuthUrl() },
            }),
          ),
        ),
      ),
    ),
  );

  public setDefaultTimezone$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CurrentUserEntityActions.fetchSuccess, CurrentUserEntityActions.updateSuccess),
        tap(action => setDefaultTimezone(action.payload.timezone)),
      ),
    { dispatch: false },
  );

  public changeCompany$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.changeCompany),
        withLatestFrom(this.store.pipe(select(AccountSelectors.currentCompany))),
        tap(([{ payload: company }, currentCompany]) => {
          if (currentCompany!.ssoActive) {
            return this.authService.redirectToSignIn(company.id);
          }
          const orgGuid = this.tellentSuiteEnabled
            ? resolveMemberOrgGuid(this.tellentCurrentUser()!, company.tsAppGuid)
            : null;

          this.companySwitch.switchCompany(orgGuid, company.id.toString());
        }),
      ),
    { dispatch: false },
  );

  public redirectToBlockedAccount$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.redirectToBlockedAccount),
        tap(() => this.authService.redirectToBlockedAccount()),
      ),
    { dispatch: false },
  );

  public reloadAfterLanguageChange$ = createEffect(
    () =>
      this.store.select(CurrentUserSelectors.data).pipe(
        pluck('preferredLanguage'),
        distinctUntilChanged(),
        skip(1),
        tap(() => this.windowRef.nativeWindow.location.reload()),
      ),
    { dispatch: false },
  );

  public signOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.signOut),
        tap(({ payload }) => {
          this.authService.clearAccessToken();
          this.authService.redirectToSignOut(payload?.afterSignOutUrl);
        }),
      ),
    { dispatch: false },
  );
}
